import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Dialog,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import useAppNavigation from '~/hooks/useAppNavigation';

import { SpotlightEntry, useSpotlightRoutes } from './SpotlightProvider';

const RECENT_PAGES_KEY = 'spotlight_recent_pages';
const MAX_RECENT_PAGES = 8;
export const OPEN_SPOTLIGHT_EVENT = 'open-admin-spotlight';

export function openSpotlight() {
  document.dispatchEvent(new Event(OPEN_SPOTLIGHT_EVENT));
}

function parseRecentPages(raw: string | null): SpotlightEntry[] {
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e: unknown) =>
        typeof (e as SpotlightEntry)?.path === 'string' && typeof (e as SpotlightEntry)?.label === 'string',
    );
  } catch {
    return [];
  }
}

function useRecentPages() {
  const [recentPages, setRecentPages] = useState<SpotlightEntry[]>(() =>
    parseRecentPages(localStorage.getItem(RECENT_PAGES_KEY)),
  );

  useEffect(() => {
    function handleStorageEvent(event: StorageEvent) {
      if (event.key === RECENT_PAGES_KEY) {
        setRecentPages(parseRecentPages(event.newValue));
      }
    }
    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
  }, []);

  const addRecentPage = useCallback((entry: SpotlightEntry) => {
    setRecentPages((prev) => {
      const filtered = prev.filter((p) => p.path !== entry.path);
      const updated = [entry, ...filtered].slice(0, MAX_RECENT_PAGES);
      try {
        localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(updated));
      } catch {
        // ignore storage errors
      }
      return updated;
    });
  }, []);

  return { recentPages, addRecentPage };
}

const Kbd = styled('kbd')(({ theme }) => ({
  padding: theme.spacing(0.125, 0.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  fontSize: theme.typography.caption.fontSize,
}));

export default function Spotlight() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const { push } = useAppNavigation();
  const getRoutes = useSpotlightRoutes();
  const { recentPages, addRecentPage } = useRecentPages();

  const isSearching = deferredQuery.trim().length > 0;

  const results = useMemo(() => {
    const allRoutes = getRoutes();
    if (!deferredQuery.trim()) {
      // Filter out stale recent pages whose paths no longer exist in the registry
      // (e.g. after a route rename).
      const validPaths = new Set(allRoutes.map((r) => r.path));
      return recentPages.filter((p) => validPaths.has(p.path));
    }

    const terms = deferredQuery.toLowerCase().split(/\s+/).filter(Boolean);
    return allRoutes.filter((route) => {
      const searchText = `${route.label} ${route.path} ${route.section} ${route.keywords ?? ''}`.toLowerCase();
      return terms.every((term) => searchText.includes(term));
    });
  }, [deferredQuery, getRoutes, recentPages]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  const navigateTo = useCallback(
    (entry: SpotlightEntry) => {
      addRecentPage(entry);
      handleClose();
      push(entry.path);
    },
    [addRecentPage, handleClose, push],
  );

  useEffect(() => {
    const openSearch = () => {
      setOpen(true);
    };

    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setOpen((prev) => {
          if (prev) {
            setQuery('');
            setSelectedIndex(0);
            return false;
          }
          return true;
        });
      }
    }

    document.addEventListener(OPEN_SPOTLIGHT_EVENT, openSearch);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener(OPEN_SPOTLIGHT_EVENT, openSearch);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [deferredQuery]);

  useEffect(() => {
    if (!listRef.current) return;
    const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          event.preventDefault();
          if (results.length > 0) {
            navigateTo(results[selectedIndex]);
          }
          break;
      }
    },
    [results, selectedIndex, navigateTo],
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            position: 'fixed',
            top: '15%',
            m: 0,
            maxHeight: '60vh',
            borderRadius: 2,
          },
        },
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.3)' },
        },
      }}
    >
      <TextField
        autoFocus
        fullWidth
        placeholder="Search pages..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleInputKeyDown}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': { border: 'none' },
          },
          borderBottom: 1,
          borderColor: 'divider',
        }}
      />

      <List ref={listRef} sx={{ overflow: 'auto', py: 0.5 }} subheader={<li />}>
        {results.length === 0 && !isSearching && (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">No recently opened pages</Typography>
          </Box>
        )}

        {results.length === 0 && isSearching && (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">No pages found</Typography>
          </Box>
        )}

        {results.length > 0 && (
          <>
            <ListSubheader
              sx={{ lineHeight: '32px', fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              {isSearching ? 'Results' : 'Recently Opened'}
            </ListSubheader>
            <Divider />
          </>
        )}

        {results.map((route, index) => (
          <ListItem key={route.path} disablePadding data-index={index}>
            <ListItemButton selected={index === selectedIndex} onClick={() => navigateTo(route)} sx={{ py: 1, px: 2 }}>
              {!isSearching && (
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <AccessTimeIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </ListItemIcon>
              )}
              <ListItemText
                primary={route.label}
                secondary={route.path}
                slotProps={{
                  secondary: {
                    sx: { fontSize: '0.75rem', color: 'text.disabled' },
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                {route.section}
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          <Kbd>↑↓</Kbd> navigate
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Kbd>↵</Kbd> open
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Kbd>esc</Kbd> close
        </Typography>
      </Box>
    </Dialog>
  );
}
